import { getClientAuthProvider, getAppUrl } from './auth-provider-config'

export function getAuthUrl() {
  return process.env.NODE_ENV === 'production'
    ? 'https://auth.cloudshuttle.com.au'
    : 'https://dev-auth.cloudshuttle.com.au'
}

export interface AuthTokens {
  access_token: string
  refresh_token?: string
  id_token?: string
  expires_in: number
  token_type: string
}

// Helper to get a cookie value
export function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

// Helper to set a cookie
export function setCookie(name: string, value: string, options: Record<string, any> = {}): void {
  if (typeof window === 'undefined') return

  const defaultOptions = {
    path: '/',
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
  }

  const cookieOptions = { ...defaultOptions, ...options }
  const cookieString = Object.entries(cookieOptions).reduce(
    (acc, [key, value]) => {
      if (value === true) return `${acc}; ${key}`
      if (value !== false) return `${acc}; ${key}=${value}`
      return acc
    },
    `${name}=${encodeURIComponent(value)}`
  )

  document.cookie = cookieString
}

// Helper to delete a cookie
export function deleteCookie(name: string): void {
  if (typeof window === 'undefined') return
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

// Store auth tokens in cookies
export function saveTokens(tokens: AuthTokens): void {
  if (typeof window === 'undefined') return

  // Store tokens in cookies
  setCookie('_cs_access_token', tokens.access_token, { maxAge: tokens.expires_in })
  setCookie('_cs_token_timestamp', Date.now().toString(), { maxAge: tokens.expires_in })

  if (tokens.refresh_token) {
    // Refresh token usually has a longer lifespan
    setCookie('_cs_refresh_token', tokens.refresh_token, { maxAge: 30 * 24 * 60 * 60 }) // 30 days
  }

  if (tokens.id_token) {
    setCookie('_cs_id_token', tokens.id_token, { maxAge: tokens.expires_in })
  }

  // Dispatch a custom event that our auth provider can listen for
  const authEvent = new CustomEvent('auth_state_changed', { detail: { isAuthenticated: true } })
  window.dispatchEvent(authEvent)
}

// Retrieve tokens from cookies
export function getTokens(): AuthTokens | null {
  if (typeof window === 'undefined') return null

  const access_token = getCookie('_cs_access_token')
  if (!access_token) return null

  return {
    access_token,
    refresh_token: getCookie('_cs_refresh_token') || undefined,
    id_token: getCookie('_cs_id_token') || undefined,
    expires_in: 3600, // Default expiry if not available
    token_type: 'Bearer',
  }
}

// Clear tokens on logout
export function clearTokens(): void {
  if (typeof window === 'undefined') return

  deleteCookie('_cs_access_token')
  deleteCookie('_cs_refresh_token')
  deleteCookie('_cs_id_token')
  deleteCookie('_cs_token_timestamp')

  // Dispatch a custom event that our auth provider can listen for
  const authEvent = new CustomEvent('auth_state_changed', { detail: { isAuthenticated: false } })
  window.dispatchEvent(authEvent)
}

// Check if tokens exist and are valid
export function isAuthenticated(): boolean {
  const tokens = getTokens()
  const timestamp = getCookie('_cs_token_timestamp')

  if (!tokens || !timestamp) {
    return false
  }

  // Check if token is expired (considering a buffer)
  const expiryTime = parseInt(timestamp) + 3600 * 1000 - 60000 // 1 minute buffer
  return Date.now() < expiryTime
}

// Get user profile info from the id_token
export function getUserProfile(): any {
  const tokens = getTokens()
  if (!tokens?.id_token) return null

  try {
    // Decode the JWT payload (second part of the token)
    const base64Url = tokens.id_token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error parsing ID token:', error)
    return null
  }
}

/** Path to begin login (Zitadel or legacy OpenAuth). */
export function getLoginPath(returnTo?: string): string {
  if (getClientAuthProvider() === 'zitadel') {
    const path = '/auth/login'
    if (returnTo) {
      return `${path}?returnTo=${encodeURIComponent(returnTo)}`
    }
    return path
  }
  return `${getAuthUrl()}/authorize?provider=google&redirect_uri=${encodeURIComponent(
    `${getAppUrl().replace(/\/$/, '')}/api/callback`
  )}`
}

// Start authentication flow with a specific provider
export function loginWithProvider(provider: string): void {
  if (getClientAuthProvider() === 'zitadel') {
    const returnTo = window.location.pathname + window.location.search
    window.location.href = getLoginPath(returnTo)
    return
  }

  const redirectUri = `${window.location.origin}/api/callback`
  const authUrl = `${getAuthUrl()}/authorize?provider=${provider}&redirect_uri=${encodeURIComponent(redirectUri)}`
  window.location.href = authUrl
}

/** Logout URL for current auth provider. */
export function getLogoutPath(): string {
  return getClientAuthProvider() === 'zitadel' ? '/auth/logout' : '/api/logout'
}

// Exchange authorization code for tokens
export async function exchangeCodeForTokens(code: string): Promise<AuthTokens> {
  const redirectUri = `${window.location.origin}/api/callback`

  const response = await fetch(`${getAuthUrl()}/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      redirect_uri: redirectUri,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(`Token exchange failed: ${errorData.error || response.statusText}`)
  }

  const tokens = await response.json()
  return tokens
}
